import { t_dataName } from './../../../../services/apiHandler/basicMachineFunctions/basic-machine-functions.service';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import {
    DidDataRetrievalService,
    t_DataIdentifier,
    t_DataPoint,
} from 'src/app/services/apiHandler/did-data-retrieval/did-data-retrieval.service';
import { GatewayConnectionService } from 'src/app/services/apiHandler/gateway-connection/gateway-connection.service';
import { joystickDataConfig, t_JoystickButtonDataset, visualizationJoystickConfig } from '../data/joystick-datanames';
import { DidConfigService } from 'src/app/services/apiHandler/did-config/did-config.service';
import { Subject, interval, takeUntil } from 'rxjs';
import { dataIdentifierEqual } from 'src/app/services/helper/did-handling-helper';

@Component({
  selector: 'app-joystick-movements-multi',
  templateUrl: './joystick-movements-multi.component.html',
  styleUrls: ['./joystick-movements-multi.component.scss']
})
export class JoystickMovementsMultiComponent implements OnInit, OnDestroy {

  public dataQueryList: t_JoystickButtonDataset = JSON.parse(JSON.stringify(visualizationJoystickConfig));
  public queryFrequency: number = 300;
  public loadedAndQueryStarting: boolean = false;
  public pollingFrequency: number;
  private destroy$: Subject<void> = new Subject<void>();
  public joystickDatasets: t_JoystickDataset[] = [];
  private destroyed: boolean = false;

  currentGear: string = 'N';
  isActive: boolean = true;
  
  constructor(private gatewayConnectionService: GatewayConnectionService, private didConfigService: DidConfigService, private dataRetrieval: DidDataRetrievalService) {
    if (this.queryFrequency <= 65) {
      this.pollingFrequency = 50;
    } else {
      this.pollingFrequency = parseFloat((this.queryFrequency * 0.75).toFixed(0));
    }
  }
  
  ngOnInit(): void {
    this.setupServices().then(() => {
      if(!this.destroyed) {
        this.dataRetrieval.initService();
        this.getDataIdentifiersToDataNamesFromConfig();
        this.postDataQueryListToBackendDidList().then(() => {
          if(this.destroyed){
            this.ngOnDestroy();
          }
          this.dataRetrieval.startDataQuery();
          this.loadedAndQueryStarting = true;
          this.setupJoystickDatasets();
          this.startPeriodicPolling();
        });
      }
    });
  }
  
  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.dataRetrieval.stopSendQueryAliveState();
    this.dataRetrieval.stopDataQuery();
    this.dataRetrieval.cleanDataPoints();
    this.dataQueryList.dataPoints.forEach(data => {
      this.didConfigService.removeDidFromQueryList({ecu_id: data.dataName.identifier!.ecu_id, data_id: data.dataName.identifier!.data_id}).subscribe();
    });
  }
  
  private setupServices(): Promise<void> {
    return new Promise((resolve) => {
      this.gatewayConnectionService.establishConnectionToGateway().then(
        () => {
          resolve();
        },
        () => {
          console.error('ERROR');
        }
      );
    });
  }
  
  private getDataIdentifiersToDataNamesFromConfig() {
    this.dataQueryList.dataPoints.forEach((datapoint) => {
      datapoint.dataName.identifier = this.didConfigService.getDataIdentifierOfDataName(datapoint.dataName.name);
      datapoint.dataName.didInformation = this.didConfigService.getDidInformation(datapoint.dataName.identifier);
    });
    this.dataQueryList.dataPoints = this.dataQueryList.dataPoints
      .map((dataPoint) => dataPoint)
      .filter((dataPoint) => dataPoint.dataName.identifier?.ecu_id !== undefined);
    if(this.destroyed) {
      this.ngOnDestroy();
    }
  }
  
  private postDataQueryListToBackendDidList(): Promise<void> {
    return new Promise((resolve) => {
      let dataIdentifierList: t_DataIdentifier[] = this.dataQueryList.dataPoints
        .map((dataPoint) => dataPoint.dataName.identifier)
        .filter((identifier) => identifier?.ecu_id !== undefined) as t_DataIdentifier[];
      this.didConfigService.postNewDidQueryList(dataIdentifierList).subscribe({
        complete: () => {
          resolve();
        },
      });
    });
  }
  
  private setupJoystickDatasets(): void {
    this.joystickDatasets = joystickDataConfig;
  
    this.joystickDatasets.forEach((joystick) => {
      joystick.forwardBackward.dataname.identifier = this.didConfigService.getDataIdentifierOfDataName(joystick.forwardBackward.dataname.name);
      joystick.leftRight.dataname.identifier = this.didConfigService.getDataIdentifierOfDataName(joystick.leftRight.dataname.name);
    });
    this.joystickDatasets = this.joystickDatasets
      .map((dataPoint) => dataPoint)
      .filter((dataPoint) => dataPoint.forwardBackward.dataname.identifier?.ecu_id !== undefined);
    this.joystickDatasets = this.joystickDatasets
      .map((dataPoint) => dataPoint)
      .filter((dataPoint) => dataPoint.leftRight.dataname.identifier?.ecu_id !== undefined);
  }
  
  private startPeriodicPolling() {
    interval(this.pollingFrequency)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getDataFromBackend();
      });
  }
  
  private getDataFromBackend() {
    // Get all Dids Values, because did list was Posted, so no other dids are present in the list
    this.dataRetrieval.getAllDidsValues().subscribe((response) => {
      if (response.body != null) {
        this.processRawData(response.body);
      }
    });
  }
  
  private processRawData(rawData: t_DataPoint[]) {
    this.joystickDatasets.forEach((joystick) => {
      // Retrieve Forward Backward Value
      let forwardBackwardIndex: number = rawData.findIndex((datapoint) => {
        return (
          datapoint.identifier.ecu_id == joystick.forwardBackward.dataname.identifier?.ecu_id &&
          datapoint.identifier.data_id == joystick.forwardBackward.dataname.identifier?.data_id
        );
      });
  
      if (rawData[forwardBackwardIndex].data != undefined) {
        let normalizedValue: number = this.normalizedValue(
          rawData[forwardBackwardIndex].data!.value[0],
          this.dataQueryList.dataPoints[forwardBackwardIndex].dataName.didInformation!.conversionFactor,
          this.dataQueryList.dataPoints[forwardBackwardIndex].dataName.didInformation!.conversionOffset
        );
        joystick.forwardBackward.value = normalizedValue;
      }
  
      // Retrieve Left Right Value
      let leftRightIndex: number = rawData.findIndex((datapoint) => {
        return (
          datapoint.identifier.ecu_id == joystick.leftRight.dataname.identifier?.ecu_id &&
          datapoint.identifier.data_id == joystick.leftRight.dataname.identifier?.data_id
        );
      });
  
      if (rawData[leftRightIndex].data != undefined) {
        let normalizedValue: number = this.normalizedValue(
          rawData[leftRightIndex].data!.value[0],
          this.dataQueryList.dataPoints[leftRightIndex].dataName.didInformation!.conversionFactor,
          this.dataQueryList.dataPoints[leftRightIndex].dataName.didInformation!.conversionOffset
        );
        joystick.leftRight.value = normalizedValue;
      }
    });

    // Retrieve Buttons, Slider and Gear
    rawData.forEach((dataPoint) => {
      if (!dataPoint.data || new Date(dataPoint.data.timestamp).getTime() < 0) {
        return;
      }
      let indexInDataset: number = this.findIndexForIdentifierDataset(dataPoint.identifier);
      let normalizedValue: number = this.normalizedValue(
        dataPoint.data.value[0],
        this.dataQueryList.dataPoints[indexInDataset].dataName.didInformation!.conversionFactor,
        this.dataQueryList.dataPoints[indexInDataset].dataName.didInformation!.conversionOffset
      );
      this.dataQueryList.dataPoints[indexInDataset].value = normalizedValue;
      //Slider
      let sliderSet = [2,3,4,5]
      if(sliderSet.includes(indexInDataset)) {
        let newValue = this.calcValueInPercent(normalizedValue);
        this.dataQueryList.dataPoints[indexInDataset].value = newValue;
      }
      /*if(this.dataQueryList.dataPoints[indexInDataset].dataName.name == 'JoystickRightThumbWheel') {
        let newValue = this.calcValueInPercent(normalizedValue);
        this.dataQueryList.dataPoints[indexInDataset].value = newValue;
      }
      if(this.dataQueryList.dataPoints[indexInDataset].dataName.name == 'JoystickRightSliderBack') {
        let newValue = this.calcValueInPercent(normalizedValue);
        this.dataQueryList.dataPoints[indexInDataset].value = newValue;
      }
      if(this.dataQueryList.dataPoints[indexInDataset].dataName.name == 'JoystickRightFingertipUpDown') {
        let newValue = this.calcValueInPercent(normalizedValue);
        this.dataQueryList.dataPoints[indexInDataset].value = newValue;
      }
      if(this.dataQueryList.dataPoints[indexInDataset].dataName.name == 'JoystickRightFingertipLeftRight') {
        let newValue = this.calcValueInPercent(normalizedValue);
        this.dataQueryList.dataPoints[indexInDataset].value = newValue;
      }*/
      //Gear
      if(indexInDataset == 12) {
        this.handleGear(normalizedValue);
      }
      /*if(this.dataQueryList.dataPoints[indexInDataset].dataName.name == 'JoystickRightGearSelection') {
        this.handleGear(normalizedValue);
      }*/
    });
  }

  private findIndexForIdentifierDataset(searchIdentifier: t_DataIdentifier): number {
    let index: number = this.dataQueryList.dataPoints.findIndex((datapoint) => {
      if (datapoint.dataName.identifier) {
        return dataIdentifierEqual(datapoint.dataName.identifier, searchIdentifier);
      } else {
        return false;
      }
    });
    return index;
  }
  
  private normalizedValue(value: number, factor: number, offset: number) {
    if(factor == 0) {
      factor = 1;
    }
    let res: number = (value * factor) + offset;
    res = parseFloat(res.toFixed(1));
    return res;
  }

  private calcValueInPercent(value: number): number {
    return (value + 100)/2;
  }

  private handleGear(value: any) {
    if(value == 0) {
      this.currentGear = 'N'; //neutral
    } else if(value == 1) {
      this.currentGear = 'F'; // forward
    } else if(value == -1) {
      this.currentGear = 'R'; //reverse
    }
  }
}
  
  export type t_JoystickDataset = {
    joystickName: string;
    forwardBackward: {
      dataname: t_dataName;
      value: number;
    };
    leftRight: {
      dataname: t_dataName;
      value?: number;
    };
  };
  
